// Security Headers component for CSP and other security headers
import { useEffect } from 'react';

interface SecurityHeadersProps {
  enableCSP?: boolean;
  enableHSTS?: boolean;
  enableXFrameOptions?: boolean;
  isAdminPage?: boolean;
  preventIndexing?: boolean;
  enableFrameAncestors?: boolean;
}

export const SecurityHeaders: React.FC<SecurityHeadersProps> = ({
  enableCSP = true,
  enableHSTS = true,
  enableXFrameOptions = true,
  isAdminPage = false,
  preventIndexing = false,
  enableFrameAncestors = false
}) => {
  useEffect(() => {
    const applySecurityHeaders = () => {
      // Content Security Policy
      if (enableCSP) {
        const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
        if (!cspMeta) {
          const meta = document.createElement('meta');
          meta.setAttribute('http-equiv', 'Content-Security-Policy');
          meta.setAttribute('content', generateCSP());
          document.head.appendChild(meta);
        }
      }

      // X-Frame-Options is removed - it should only be set via HTTP headers, not meta tags

      // Additional security headers via meta tags
      addSecurityMeta('X-Content-Type-Options', 'nosniff');
      addSecurityMeta('Referrer-Policy', 'strict-origin-when-cross-origin');
      addSecurityMeta('Permissions-Policy', 'camera=(), microphone=(), geolocation=()');

      // Admin page specific security headers
      if (isAdminPage) {
        addSecurityMeta('Cache-Control', 'no-cache, no-store, must-revalidate');
        addSecurityMeta('Pragma', 'no-cache');
        addSecurityMeta('Expires', '0');
        addRobotsMeta('noindex, nofollow, noarchive, nosnippet, noimageindex');
      }

      // Prevent search engine indexing
      if (preventIndexing || isAdminPage) {
        addRobotsMeta('noindex, nofollow, noarchive, nosnippet, noimageindex');
      }
    };

    applySecurityHeaders();
  }, [enableCSP, enableHSTS, enableXFrameOptions, isAdminPage, preventIndexing]);

  return null;
};

const generateCSP = (): string => {
  // Use environment variable for Supabase URL, fallback to production
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://voyrsahkomcidcwjxcst.supabase.co';

  // Allow local development URLs - check for localhost, 127.0.0.1, or private network IPs (192.168.x.x, 10.x.x.x, 172.x.x.x)
  const isLocalDev = supabaseUrl.includes('127.0.0.1') ||
    supabaseUrl.includes('localhost') ||
    supabaseUrl.includes('192.168.') ||
    supabaseUrl.includes('10.') ||
    (supabaseUrl.includes('172.') && !supabaseUrl.includes('supabase.co'));

  const localDevUrls = isLocalDev ? 'http://127.0.0.1:* http://localhost:* http://192.168.*:* http://10.*:* http://172.*:* ws://127.0.0.1:* ws://localhost:* ws://192.168.*:* ws://10.*:* ws://172.*:*' : '';

  return [
    "default-src 'self'",
    `connect-src 'self' ${supabaseUrl} ${localDevUrls} wss://*.supabase.co https://*.upay.co.il https://*.invoice4u.co.il https://*.grow.link https://*.grow.business https://www.google-analytics.com https://analytics.google.com https://www.googletagmanager.com https://www.clarity.ms https://*.clarity.ms https://*.bing.com https://*.sentry.io https://o4509938802229248.ingest.de.sentry.io https://*.enable.co.il https://connect.facebook.net https://www.facebook.com https://*.facebook.com https://analytics.tiktok.com https://*.tiktok.com https://*.tiktokw.us https://googleads.g.doubleclick.net https://www.googleadservices.com https://www.google.com blob: data:`,
    "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://*.upay.co.il https://*.invoice4u.co.il https://*.grow.link https://*.grow.business https://www.googletagmanager.com https://www.google-analytics.com https://*.enable.co.il https://www.clarity.ms https://*.clarity.ms https://*.sentry.io https://connect.facebook.net https://www.facebook.com https://*.facebook.com https://analytics.tiktok.com https://*.tiktok.com https://www.googleadservices.com https://www.google.com blob: data:",
    "style-src 'self' 'unsafe-inline' https://*.googleapis.com https://*.upay.co.il https://*.invoice4u.co.il https://*.grow.link https://*.grow.business https://*.enable.co.il",
    `img-src 'self' data: https: blob: ${isLocalDev ? 'http://127.0.0.1:* http://localhost:*' : ''} https://*.enable.co.il https://www.facebook.com`,
    "font-src 'self' data: https://*.googleapis.com https://*.upay.co.il https://*.invoice4u.co.il https://*.grow.link https://*.grow.business https://*.enable.co.il",
    "object-src 'none'",
    "base-uri 'self'",
    "form-action 'self' https://*.upay.co.il https://*.invoice4u.co.il https://*.grow.link https://*.grow.business",
    `frame-src 'self' https://excess-market.co.il https://www.excess-market.co.il https://*.upay.co.il https://*.invoice4u.co.il https://*.grow.link https://*.grow.business ${supabaseUrl} ${localDevUrls} https://*.enable.co.il https://vercel.live`,
    "child-src 'self' blob: data: https://*.upay.co.il https://*.invoice4u.co.il https://*.grow.link https://*.grow.business https://*.enable.co.il",
    "worker-src 'self' blob: data: https://*.enable.co.il",
    `media-src 'self' ${supabaseUrl} blob: data:`,
    "manifest-src 'self'"
  ].join('; ');
};

const addSecurityMeta = (name: string, content: string) => {
  const existing = document.querySelector(`meta[http-equiv="${name}"]`);
  if (!existing) {
    const meta = document.createElement('meta');
    meta.setAttribute('http-equiv', name);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
};

const addRobotsMeta = (content: string) => {
  // Remove existing robots meta tags
  const existingRobots = document.querySelectorAll('meta[name="robots"], meta[name="googlebot"]');
  existingRobots.forEach(meta => meta.remove());

  // Add comprehensive robots blocking
  const robotsMeta = document.createElement('meta');
  robotsMeta.setAttribute('name', 'robots');
  robotsMeta.setAttribute('content', content);
  document.head.appendChild(robotsMeta);

  const googlebotMeta = document.createElement('meta');
  googlebotMeta.setAttribute('name', 'googlebot');
  googlebotMeta.setAttribute('content', content);
  document.head.appendChild(googlebotMeta);
};